import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type SystemSettings } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminSettings() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  if (!me.isLoading && !canManageQuestions(me.data)) return <Navigate to="/admin" replace />;
  const settings = useQuery({ queryKey: ["system-settings"], queryFn: () => adminApi.getSettings() });

  const [form, setForm] = useState<SystemSettings>({
    cameraIntervalMinutes: 0,
    cameraIntervalSeconds: 35,
    cameraEnabledByDefault: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => adminApi.updateSettings(form),
    onSuccess: (v) => {
      setForm(v);
      setError(null);
      setSavedAt(new Date());
      qc.invalidateQueries({ queryKey: ["system-settings"] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Save failed."),
  });

  const totalSeconds = form.cameraIntervalMinutes * 60 + form.cameraIntervalSeconds;

  return (
    <main className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-bold text-brand-ink">System settings</h1>

      <div className="card p-6 space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-brand-ink">Camera snapshot interval</h2>
          <p className="mt-1 text-sm text-brand-ink/60">
            How often the candidate's camera takes a snapshot during the exam. Applies as the
            default when a new quiz is created. Existing quizzes keep their own value —
            edit them individually if you want to update those.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:max-w-sm">
            <div>
              <label className="field-label" htmlFor="camMin">Minutes</label>
              <input id="camMin" type="number" min={0} max={60} className="field-input"
                value={form.cameraIntervalMinutes}
                onChange={(e) => setForm({ ...form, cameraIntervalMinutes: Number(e.target.value) })} />
            </div>
            <div>
              <label className="field-label" htmlFor="camSec">Seconds</label>
              <input id="camSec" type="number" min={0} max={59} className="field-input"
                value={form.cameraIntervalSeconds}
                onChange={(e) => setForm({ ...form, cameraIntervalSeconds: Number(e.target.value) })} />
            </div>
          </div>
          <div className="mt-2 text-xs text-brand-ink/60">
            Total: <b>{totalSeconds}</b> seconds. Minimum 5, maximum 3600.
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="text-brand-ink/60">Presets:</span>
            {[5, 15, 30, 60, 120, 300].map((s) => (
              <button key={s} type="button"
                className="rounded-pill bg-brand-light px-3 py-1 hover:bg-brand hover:text-white"
                onClick={() => setForm({
                  ...form,
                  cameraIntervalMinutes: Math.floor(s / 60),
                  cameraIntervalSeconds: s % 60,
                })}>
                {s < 60 ? `${s}s` : `${s / 60}m`}
              </button>
            ))}
          </div>
        </section>

        <section className="border-t border-brand-ink/10 pt-6">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" className="accent-brand"
              checked={form.cameraEnabledByDefault}
              onChange={(e) => setForm({ ...form, cameraEnabledByDefault: e.target.checked })} />
            <span>Camera enabled by default on new quizzes</span>
          </label>
          <p className="mt-1 text-xs text-brand-ink/60">
            If unchecked, new quizzes start with the camera disabled and admins must turn it on
            per quiz.
          </p>
        </section>

        {error && <div className="text-sm text-brand">{error}</div>}
        {savedAt && !save.isPending && (
          <div className="text-sm text-emerald-700">Saved at {savedAt.toLocaleTimeString()}.</div>
        )}

        <div className="flex justify-end">
          <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </main>
  );
}
